'use client';

import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { InterfaceAppProviders } from '../../../components/app/InterfaceAppProviders';
import { APP_RESOURCES_STRATEGY_VS_HOOK } from '../../../components/app/InterfaceAppResourceLoader/constants/resourceStrategyVsHook';
import { InterfacePageProviders } from '../../../components/page/InterfacePageProviders';
import { PAGE_RESOURCES_STRATEGY_VS_HOOK } from '../../../components/page/InterfacePageResourceLoader/constants/resourceStrategyVsHook';
import type { InterfaceResourceStrategyType } from '../../../context/InterfaceResourceStrategyProvider/constants';
import type BlocksRegistry from '../../../context/RegistryProvider/BlocksRegistry';
import type { GlobalAppStateStoreType } from '../../../stores/app/GlobalAppStateStore';
import { InterfaceModes } from '../../../stores/InterfaceStore';
import {
  useWalkthroughManagerStore,
  WalkthroughProcessState,
} from '../../../stores/WalkthroughManagerStore';
import { WalkthroughStepRendererMap } from './components/WalkthroughStepRenderer';

type PageWalkthroughGuideProps = {
  client: GlobalAppStateStoreType['client'];
  mode?: InterfaceModes;
  registry: BlocksRegistry;
  strategy: InterfaceResourceStrategyType;
};

function WalkthroughGuide(props: PageWalkthroughGuideProps) {
  const { client, mode = InterfaceModes.RUNNER, registry, strategy } = props;
  const interfaceId = useWalkthroughManagerStore().hooks.useInstanceId();
  const activeWalkthroughId = useWalkthroughManagerStore().hooks.useActiveWalkthroughId();
  const device = useWalkthroughManagerStore().hooks.useDevice();
  const processState = useWalkthroughManagerStore().hooks.useProcessState();

  // don't run any resource hooks until we know these, per logic gates below
  if (!activeWalkthroughId || processState !== WalkthroughProcessState.walkthrough_active) {
    return null;
  }

  const WalkthroughGuideComponent = WalkthroughStepRendererMap[device];

  return (
    <WalkthroughGuideDataLoader
      activeWalkthroughId={activeWalkthroughId}
      client={client}
      interfaceId={interfaceId}
      mode={mode}
      registry={registry}
      strategy={strategy}
      WalkthroughGuideComponent={WalkthroughGuideComponent}
    />
  );
}

interface WalkthroughGuideDataLoaderProps extends PageWalkthroughGuideProps {
  interfaceId: string;
  activeWalkthroughId: string;
  WalkthroughGuideComponent: React.ComponentType;
}

function WalkthroughGuideDataLoader({
  client,
  mode = InterfaceModes.RUNNER,
  registry,
  strategy,
  interfaceId,
  activeWalkthroughId,
  WalkthroughGuideComponent,
}: WalkthroughGuideDataLoaderProps) {
  const useAppResourceStrategy = APP_RESOURCES_STRATEGY_VS_HOOK[strategy];
  const usePageResourceStrategy = PAGE_RESOURCES_STRATEGY_VS_HOOK[strategy];

  const {
    envVariables,
    interfaceRecord,
    isLoading: appIsLoading,
    error: appError,
    permissions,
    commonNamespace,
    dataSources: appDataSources,
  } = useAppResourceStrategy({
    interfaceId,
    client,
    mode,
  });

  const {
    dataSources,
    error: pageError,
    isLoading: pageIsLoading,
    interfacePage,
    interfacePageNamespace,
    // eslint-disable-next-line react-compiler/react-compiler -- We need to use the `useResourceStrategy` hook
  } = usePageResourceStrategy({
    interfaceId,
    interfaceComponent: {
      id: activeWalkthroughId,
      type: InterfacePageEntityComponentType.WALKTHROUGH,
    },
    client,
    mode,
  });

  const isLoading = pageIsLoading || appIsLoading;
  const error = pageError || appError;

  if (isLoading || error) {
    return null;
  }

  if (!interfaceRecord || !interfacePage) {
    return null;
  }

  return (
    <InterfaceAppProviders
      client={client}
      commonNamespace={commonNamespace}
      dataSources={appDataSources}
      envVariables={envVariables}
      interfaceRecord={interfaceRecord}
      permissions={permissions}
      registry={registry}
    >
      <InterfacePageProviders
        dataSources={dataSources}
        disableCSSScoping
        interfacePageNamespace={interfacePageNamespace}
        interfaceRecord={interfaceRecord}
        mode={mode}
        page={interfacePage}
      >
        <WalkthroughGuideComponent />
      </InterfacePageProviders>
    </InterfaceAppProviders>
  );
}

export default WalkthroughGuide;
